import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Sidebar from "@/components/Sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Package, 
  BarChart3, 
  Menu,
  Clock,
  Truck,
  Wrench,
  ArrowRight,
  Info,
  CheckCircle2,
  Send
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SolutionDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { bundleId, solutionId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    fetchBundle();
  }, [bundleId, solutionId]);

  const fetchBundle = async () => {
    try {
      const id = bundleId || solutionId;
      const endpoint = bundleId ? `${API}/bundles/${id}` : `${API}/solutions/${id}`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setBundle(response.data);
    } catch (error) {
      console.error("Error fetching bundle:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOffer = async () => {
    if (!bundle) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/offers/request`, {
        bundle_id: bundle.bundle_id,
        support_package_id: null
      }, { withCredentials: true });
      
      setRequestId(response.data.request_id);
      setShowThankYou(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit offer request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Bundle not found</p>
          <Button onClick={() => navigate("/solutions")}>Back to Solutions</Button>
        </div>
      </div>
    );
  }

  const savings = bundle.market_price - bundle.base_price;
  const savingsPercent = Math.round((1 - bundle.base_price / bundle.market_price) * 100);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold">Solution Details & Availability</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left column - Bundle details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Bundle header */}
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-start gap-6">
                  <div className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                    <Package className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold mb-2">{bundle.name}</h2>
                    <p className="text-muted-foreground">{bundle.description}</p>
                  </div>
                </div>
              </div>

              {/* Details table */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h3 className="font-heading font-semibold">Details</h3>
                </div>
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="p-4 text-left">Item</th>
                      <th className="p-4 text-right w-20">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundle.items?.map((item, i) => (
                      <tr key={i}>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            {item.specs && (
                              <p className="text-sm text-muted-foreground">{item.specs}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-right font-mono">{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pricing Overview */}
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <h3 className="font-heading font-semibold">Pricing Overview</h3>
                
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Base Price:</span>
                  <span className="text-2xl font-bold font-heading">AED {bundle.base_price.toLocaleString()}</span>
                </div>

                <div className="savings-highlight rounded-lg p-4 flex items-center justify-between">
                  <span className="text-[#00FF88]">Your Estimated Savings from market prices:</span>
                  <span className="text-[#00FF88] font-bold">AED {savings.toLocaleString()} ({savingsPercent}%)</span>
                </div>

                <p className="text-sm text-muted-foreground">Transparent pricing, no hidden fees.</p>
              </div>

              {/* TCO Viewer */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4">Total Cost of Ownership (TCO) Viewer</h3>
                
                <div className="bg-[#FFD60A]/10 border border-[#FFD60A]/20 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-[#FFD60A] shrink-0 mt-0.5" />
                    <p className="text-sm">View detailed TCO breakdown for this solution.</p>
                  </div>
                  <Button
                    onClick={() => navigate(`/tco?bundle=${bundle.bundle_id}`)}
                    className="bg-[#FFD60A] text-black hover:bg-[#FFD60A]/80"
                    data-testid="view-tco-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View TCO
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground mt-4">
                  Make informed choices about services and support packages.
                </p>
              </div>
            </div>

            {/* Right column - Availability & Actions */}
            <div className="space-y-6">
              {/* Availability */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4">Availability & Lead Times</h3>
                
                <div className="bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#007AFF]" />
                    <div>
                      <p className="text-sm font-medium">Estimated Lead Time: {bundle.lead_time_days} days from date of order</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-[#007AFF]" />
                    <div>
                      <p className="text-sm font-medium">Delivery: {bundle.delivery_days} business days</p>
                    </div>
                  </div>
                  {bundle.installation_days && (
                    <div className="flex items-center gap-3">
                      <Wrench className="w-5 h-5 text-[#007AFF]" />
                      <div>
                        <p className="text-sm font-medium">Installation: {bundle.installation_days} business days (if applicable)</p>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  Lead times may vary based on customization and location.
                </p>
              </div>

              {/* Features */}
              {bundle.features && bundle.features.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-heading font-semibold mb-4">Included Features</h3>
                  <ul className="space-y-2">
                    {bundle.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00FF88]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Button */}
              <Button
                size="lg"
                className="w-full bg-[#007AFF] hover:bg-[#0055CC] h-14"
                onClick={() => navigate(`/compare?bundle=${bundle.bundle_id}`)}
                data-testid="proceed-to-configuration-btn"
              >
                Proceed to Configuration
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
